import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPoojaTag,
  createPoojaTag,
  updatePoojaTag,
  deletePoojaTag
} from '../../Redux/Reducers/PoojaTagReducer';
import {
  Box, IconButton, Dialog, DialogActions, DialogContent,
  DialogTitle, Button, TablePagination, CircularProgress,
  TextField, Grid, Divider, Tooltip, DialogContentText
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BsDownload } from 'react-icons/bs';
import * as XLSX from 'xlsx';


const EMPTY_FORM = { tagName: '' };

function PoojaTagList() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialPageSize = parseInt(searchParams.get('pageSize') || '10', 10);
  const initialSearch = searchParams.get('search') || '';

  const [paginationModel, setPaginationModel] = useState({
    page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
    pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10,
  });
  const [searchText, setSearchText] = useState(initialSearch);
  const [appliedSearch, setAppliedSearch] = useState(initialSearch);
  const [exportLoading, setExportLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  // Dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const didInitUrlSyncRef = useRef(false);

  const { poojaTag = [], pagination, loading: tagLoading } =
    useSelector((state) => state?.PoojaTagReducer || {});

  // Fetch list
  const fetchList = useCallback(() => {
    const { page, pageSize } = paginationModel;
    let limit = Number(pageSize);
    if (isNaN(limit)) limit = 10;
    dispatch(fetchPoojaTag({ page: page + 1, limit, search: appliedSearch }));
  }, [dispatch, paginationModel.page, paginationModel.pageSize, appliedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // URL sync
  useEffect(() => {
    if (!didInitUrlSyncRef.current) { didInitUrlSyncRef.current = true; return; }
    const params = new URLSearchParams();
    params.set('page', String(paginationModel.page));
    params.set('pageSize', String(paginationModel.pageSize));
    if (appliedSearch) params.set('search', appliedSearch);
    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, appliedSearch, setSearchParams]);

  const handleOpenCreate = () => {
    setEditId(null);
    setEditForm(EMPTY_FORM);
    setEditOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditId(row.id);
    setEditForm({
      tagName: row.tagName || '',
    });
    setEditOpen(true);
  };

  const handleEditChange = (field, value) =>
    setEditForm((prev) => ({ ...prev, [field]: value }));

  const handleEditSubmit = async () => {
    if (!editForm.tagName.trim()) { toast.error('Tag Name is required'); return; }

    setActionLoading((prev) => ({ ...prev, edit: true }));
    try {
      const payload = {
        tagName: editForm.tagName,
      };

      if (editId) {
        await dispatch(updatePoojaTag({ id: editId, payload })).unwrap();
        toast.success('Pooja Tag updated successfully!');
      } else {
        await dispatch(createPoojaTag(payload)).unwrap();
        toast.success('Pooja Tag created successfully!');
      }
      setEditOpen(false);
      setEditForm(EMPTY_FORM);
      fetchList();
    } catch (err) {
      toast.error(err || `Failed to ${editId ? 'update' : 'create'} tag`);
    } finally {
      setActionLoading((prev) => ({ ...prev, edit: false }));
    }
  };

  const handleOpenDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleDeleteSubmit = async () => {
    if (!deleteId) return;
    setActionLoading((prev) => ({ ...prev, delete: true }));
    try {
      await dispatch(deletePoojaTag(deleteId)).unwrap();
      toast.success('Pooja Tag deleted successfully!');
      setDeleteOpen(false);
      setDeleteId(null);
      fetchList();
    } catch (err) {
      toast.error(err || 'Failed to delete tag');
    } finally {
      setActionLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleChangePage = (e, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }));
  const handleChangeRowsPerPage = (e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) });

  const handleApplySearch = () => { setAppliedSearch(searchText.trim()); setPaginationModel((prev) => ({ ...prev, page: 0 })); };
  const handleResetSearch = () => { setSearchText(''); setAppliedSearch(''); setPaginationModel({ page: 0, pageSize: 10 }); };

  const handleExport = useCallback(async () => {
    const token = localStorage.getItem('astroVani-admin-token');
    setExportLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/tag?${appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : ''}`, {
        headers: { Authorization: token },
      });
      if (!res.ok) throw new Error('Export failed');
      const result = await res.json();
      const tags = Array.isArray(result) ? result : result?.data || [];
      if (!tags.length) { toast.error('No data to export'); return; }

      const headers = ['Tag Name', 'Created At'];
      const exportRows = tags.map((t) => [
        t.tagName || '',
        t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '',
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...exportRows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'PoojaTags');
      ws['!cols'] = headers.map((h) => ({ wch: h.length + 15 }));
      XLSX.writeFile(wb, 'pooja_tags_export.xlsx');
      toast.success('Exported successfully!');
    } catch {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  }, [appliedSearch]);

  const columns = [
    { field: 'sno', headerName: 'S.No', width: 65, filterable: false, sortable: false, headerAlign: 'center', align: 'center' },
    {
      field: 'tagName', headerName: 'Tag Name', flex: 1, minWidth: 200,
      renderCell: (params) => <span style={{ fontWeight: 500 }}>{params.value || '-'}</span>,
    },
    {
      field: 'createdAt', headerName: 'Created At', width: 180, type: 'dateTime',
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      field: 'actions', headerName: 'Actions', width: 120, sortable: false, filterable: false, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <Tooltip title="Edit Tag">
            <IconButton size="small" onClick={() => handleOpenEdit(row)}
              sx={{ color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' } }}>
              <i className="icofont-edit" style={{ fontSize: '17px' }}></i>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Tag">
            <IconButton size="small" onClick={() => handleOpenDelete(row.id)}
              sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' } }}>
              <i className="icofont-trash" style={{ fontSize: '17px' }}></i>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = (poojaTag || []).map((item, index) => {
    if (!item || !item._id) return null;
    return {
      sno: paginationModel.page * paginationModel.pageSize + index + 1,
      id: item._id,
      tagName: item.tagName || '-',
      createdAt: item.createdAt ? new Date(item.createdAt) : null,
    };
  }).filter(Boolean);

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Pooja Tags" />


        <div className="card mb-3" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', padding: '20px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" sx={{ gap: 2 }}>
            <Box sx={{ flex: '2 1 220px' }}>
              <label className="form-label fw-bold small text-muted mb-1">Search</label>
              <input type="text" placeholder="Search by tag name..." value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplySearch(); }}
                className="form-control form-control-sm" />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Button variant="contained" onClick={handleApplySearch} size="small" sx={{ textTransform: 'none', height: '31px' }}>Search</Button>
              <Button variant="outlined" onClick={handleResetSearch} disabled={!appliedSearch && !searchText} size="small" sx={{ textTransform: 'none', height: '31px' }}>Reset</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Button variant="contained"
                startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <BsDownload />}
                size="small" disabled={exportLoading || tagLoading} onClick={handleExport}
                sx={{ fontSize: '13px', px: 2, py: 0.75, borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}>
                {exportLoading ? 'Exporting...' : 'Export'}
              </Button>
              <Button variant="contained" onClick={handleOpenCreate} size="small"
                sx={{ fontSize: '13px', px: 2, py: 0.75, borderRadius: '8px', textTransform: 'none', boxShadow: 'none', backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}>
                + Add Tag
              </Button>
            </Box>
          </Box>
        </div>

        <div className="row g-0">
          <div className="col-md-12">
            <div className="card" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}>
              <div className="card-body" style={{ padding: 0 }}>
                <Box sx={{ width: '100%' }}>
                  <DataGrid loading={tagLoading} columns={columns} rows={rows}
                    disableRowSelectionOnClick paginationMode="server" hideFooterPagination
                    autoHeight getRowId={(row) => row.id} sx={{ border: 'none' }} />
                </Box>
                <TablePagination component="div" count={pagination?.totalDocs || 0}
                  page={paginationModel.page} onPageChange={handleChangePage}
                  rowsPerPage={paginationModel.pageSize} onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Rows per page:" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditForm(EMPTY_FORM); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <i className={editId ? "icofont-edit" : "icofont-plus"} style={{ color: editId ? '#1976d2' : '#7b1fa2', fontSize: '20px' }}></i>
            {editId ? 'Edit Tag' : 'Add Tag'}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Tag Name *" value={editForm.tagName}
                onChange={(e) => handleEditChange('tagName', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setEditOpen(false); setEditForm(EMPTY_FORM); }} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }} disabled={actionLoading.edit}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}
            disabled={actionLoading.edit}
            startIcon={actionLoading.edit ? <CircularProgress size={16} color="inherit" /> : null}>
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1, color: '#d32f2f' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <i className="icofont-warning" style={{ fontSize: '20px' }}></i>
            Confirm Delete
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <DialogContentText>
            Are you sure you want to delete this tag? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }} disabled={actionLoading.delete}>Cancel</Button>
          <Button onClick={handleDeleteSubmit} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: '8px' }}
            disabled={actionLoading.delete}
            startIcon={actionLoading.delete ? <CircularProgress size={16} color="inherit" /> : null}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PoojaTagList;
